import { useEffect, useState } from 'react';
import { wechatApi, type ClassifiedTransaction } from '../api/client.js';
import { formatAmount, formatBusinessType } from '../components/Layout.js';
import { SkeletonRows, TableEmpty } from '../components/States.js';
import { roleCan, permHint } from '../utils/permissions.js';

type Tier = 'high' | 'medium' | 'low' | 'all';

const TIER_TABS: Array<{ key: Tier; label: string; desc: string }> = [
  { key: 'high', label: '高置信度', desc: 'AI 有把握，可批量一键确认' },
  { key: 'medium', label: '中置信度', desc: 'AI 已预填推荐，请下拉选择或修正' },
  { key: 'low', label: '低置信度', desc: '无法判断，需逐条手动指定' },
  { key: 'all', label: '全部', desc: '' },
];

const TIER_BADGE: Record<string, string> = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-red-100 text-red-700',
};

const TIER_LABEL: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

// 业务类型下拉选项（与后端分类引擎输出对齐）
const BUSINESS_OPTIONS = ['consulting', 'design', 'project', 'expense', 'training'];

export default function WechatConfirm() {
  const canConfirm = roleCan('wechat:confirm');
  const canGenerate = roleCan('journal:post');
  const [tier, setTier] = useState<Tier>('high');
  const [items, setItems] = useState<ClassifiedTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const load = async (t: Tier = tier) => {
    setLoading(true);
    setMessage('');
    try {
      const res = await wechatApi.pending(t === 'all' ? {} : { tier: t, pageSize: 100 });
      setItems(res.items.filter((i) => i.needConfirm));
      setTotal(res.total);
      setSelected(new Set());
      setOverrides({});
    } catch (e: any) {
      setMessage(`加载失败：${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tier); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tier]);

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // 高置信度：一键确认 AI 推荐
  const batchConfirmAll = async () => {
    const ids = items.filter((i) => i.businessType).map((i) => i.id);
    if (ids.length === 0) { setMessage('没有可确认的记录'); return; }
    try {
      const res = await wechatApi.batchConfirm(ids);
      setMessage(`✅ 已批量确认 ${res.confirmed} 条`);
      await load();
    } catch (e: any) {
      setMessage(`确认失败：${e.message}`);
    }
  };

  // 勾选记录批量确认（可带统一类型）
  const confirmSelected = async () => {
    if (selected.size === 0) { setMessage('请先勾选记录'); return; }
    const ids = [...selected];
    try {
      // 逐条使用各自的下拉修正值，剩余的沿用 AI 推荐
      const overridesOnly = ids.filter((id) => overrides[id]);
      const plainIds = ids.filter((id) => !overrides[id]);
      let confirmed = 0;
      for (const type of BUSINESS_OPTIONS) {
        const typeIds = overridesOnly.filter((id) => overrides[id] === type);
        if (typeIds.length > 0) {
          const res = await wechatApi.batchConfirm(typeIds, type);
          confirmed += res.confirmed;
        }
      }
      if (plainIds.length > 0) {
        const res = await wechatApi.batchConfirm(plainIds);
        confirmed += res.confirmed;
      }
      setMessage(`✅ 已确认 ${confirmed} 条`);
      await load();
    } catch (e: any) {
      setMessage(`确认失败：${e.message}`);
    }
  };

  const activeTab = TIER_TABS.find((t) => t.key === tier)!;

  // 为已确认且未生成凭证的流水批量生成记账凭证（幂等）
  const generateJournals = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await wechatApi.generateJournal();
      setMessage(
        res.generated > 0
          ? `✅ 已生成 ${res.generated} 张凭证（${res.references.join('、')}），可在「记账凭证」中查看`
          : '没有可生成凭证的流水（需先确认分类）'
      );
    } catch (e: any) {
      setMessage(`生成失败：${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const [busy, setBusy] = useState(false);

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-1">收款分类确认</h2>
      <p className="text-sm text-slate-500 mb-4">微信流水按业务自动分类，按置信度分层确认，确认结果将作为下次分类的学习样本</p>

      {/* 分层 Tabs */}
      <div className="flex gap-2 mb-4">
        {TIER_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTier(t.key)}
            className={`px-4 py-2 rounded-lg text-sm ${
              tier === t.key ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab.desc && (
        <div className="mb-3 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          💡 {activeTab.desc}
        </div>
      )}

      {message && (
        <div className="mb-3 p-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm">{message}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="w-10 px-4 py-3">
                <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} />
              </th>
              <th className="text-left px-2 py-3">时间</th>
              <th className="text-left px-2 py-3">付款方</th>
              <th className="text-left px-2 py-3">备注</th>
              <th className="text-right px-2 py-3">金额</th>
              <th className="text-left px-2 py-3">置信度</th>
              <th className="text-left px-2 py-3">AI 推荐 / 修正</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows colSpan={7} />
            ) : items.length === 0 ? (
              <TableEmpty
                colSpan={7}
                icon="🎉"
                title="该层没有待确认记录"
                hint="这一置信度档位已全部处理完毕，可切换上方其他分层查看"
              />
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
                  </td>
                  <td className="px-2 py-3 text-xs text-slate-500">{item.transactionTime.slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-2 py-3">{item.counterparty}</td>
                  <td className="px-2 py-3 text-slate-500 max-w-[160px] truncate">{item.remark}</td>
                  <td className="px-2 py-3 text-right font-mono">¥{formatAmount(item.amount)}</td>
                  <td className="px-2 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${TIER_BADGE[item.tier]}`}>
                      {TIER_LABEL[item.tier]} {(item.confidence * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <select
                      value={overrides[item.id] || item.businessType || ''}
                      onChange={(e) => setOverrides({ ...overrides, [item.id]: e.target.value })}
                      className={`border rounded px-2 py-1 text-xs bg-white ${
                        overrides[item.id] ? 'border-blue-400' : item.businessType ? 'border-slate-200' : 'border-red-300'
                      }`}
                    >
                      <option value="" disabled>{item.businessType ? formatBusinessType(item.businessType) : '选择业务类型'}</option>
                      {BUSINESS_OPTIONS.map((o) => (
                        <option key={o} value={o}>{formatBusinessType(o)}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 操作栏 */}
      <div className="flex items-center gap-3 mt-4">
        {tier === 'high' && (
          <button
            onClick={batchConfirmAll}
            disabled={!canConfirm}
            title={permHint('wechat:confirm')}
            className="btn btn-accent"
          >
            ⚡ 一键确认全部（{items.length}）
          </button>
        )}
        <button
          onClick={confirmSelected}
          disabled={selected.size === 0 || !canConfirm}
          title={permHint('wechat:confirm')}
          className={`px-4 py-2 rounded-lg text-sm text-white ${
            selected.size > 0 && canConfirm ? 'bg-brand-600 hover:bg-brand-700' : 'bg-slate-300 cursor-not-allowed'
          }`}
        >
          确认所选（{selected.size}）
        </button>
        <button
          onClick={generateJournals}
          disabled={busy || !canGenerate}
          title={permHint('journal:post')}
          className="btn btn-primary"
        >
          {busy ? '生成中...' : '📝 为已确认流水生成凭证'}
        </button>
        <button
          onClick={() => load()}
          className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm"
        >
          刷新
        </button>
      </div>
    </div>
  );
}
